import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { TextArea } from '../../components/ui/TextArea';
import { Alert } from '../../components/ui/Alert';
import { AvatarUpload } from '../../components/ui/AvatarUpload';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

export const Onboarding = () => {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Initialize form with existing profile data if they left and returned
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    job_title: profile?.job_title || '',
    company_name: profile?.company_name || '',
    phone: profile?.phone || '',
    website: profile?.website || '',
    location: profile?.location || '',
    bio: profile?.bio || '',
    avatar_url: profile?.avatar_url || ''
  });

  // Ensure formData updates if profile context loads slightly after mount
  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        full_name: profile.full_name || prev.full_name,
        job_title: profile.job_title || prev.job_title,
        company_name: profile.company_name || prev.company_name,
        phone: profile.phone || prev.phone,
        website: profile.website || prev.website,
        location: profile.location || prev.location,
        bio: profile.bio || prev.bio,
        avatar_url: profile.avatar_url || prev.avatar_url
      }));
    }
  }, [profile]);

  const handleValidation = () => {
    if (!formData.full_name.trim()) return "Full name is required.";
    if (!formData.job_title.trim()) return "Job title or professional role is required.";
    return null;
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    const validationError = handleValidation();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          ...formData,
          onboarding_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;
      
      await refreshProfile();
      setStep(3); // Move to completion step
    } catch (err) {
      setError(err.message || 'An error occurred while saving your profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto pt-8 pb-16">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-center space-x-2">
          {[1, 2, 3].map((s) => (
            <div 
              key={s} 
              className={`h-2 w-16 rounded-full transition-colors duration-300 ${
                s <= step ? 'bg-slate-900' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-200 shadow-sm relative overflow-hidden">
        
        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-8 border border-slate-100">
              <SafeIcon icon={FiIcons.FiLayers} className="text-4xl text-slate-900" />
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              Welcome to Meetoi
            </h1>
            <p className="text-lg text-slate-600 max-w-md mx-auto mb-10 leading-relaxed">
              Let's set up your professional profile. You can use this information when creating your digital networking cards.
            </p>
            <Button size="lg" onClick={() => setStep(2)} className="w-full sm:w-auto px-12">
              Get Started
            </Button>
          </div>
        )}

        {/* Step 2: Professional Profile Form */}
        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <header className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900">Your Professional Profile</h2>
              <p className="text-slate-600 mt-2">Add the details you want to reuse across your digital cards.</p>
            </header>

            {error && <Alert variant="error" message={error} className="mb-8" />}

            <form onSubmit={handleSaveProfile} className="space-y-8">
              <AvatarUpload 
                url={formData.avatar_url}
                onUpload={(url) => setFormData({ ...formData, avatar_url: url })}
              />

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <p className="text-sm font-medium text-slate-500 mb-1">Account Email</p>
                <p className="text-slate-900 font-medium">{user?.email}</p>
                <p className="text-xs text-slate-400 mt-1">Email changes can be managed in account settings later.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label={<>Full Name <span className="text-red-500">*</span></>}
                  placeholder="e.g. Jane Doe"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                />
                <Input
                  label={<>Job Title or Role <span className="text-red-500">*</span></>}
                  placeholder="e.g. Product Manager"
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  required
                />
                <Input
                  label={<span>Company <span className="text-slate-400 font-normal">(Optional)</span></span>}
                  placeholder="e.g. Acme Corp"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                />
                <Input
                  label={<span>Phone Number <span className="text-slate-400 font-normal">(Optional)</span></span>}
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
                <Input
                  label={<span>Website <span className="text-slate-400 font-normal">(Optional)</span></span>}
                  type="url"
                  placeholder="https://example.com"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
                <Input
                  label={<span>Location <span className="text-slate-400 font-normal">(Optional)</span></span>}
                  placeholder="e.g. San Francisco, CA"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </div>

              <TextArea
                label={<span>Professional Bio <span className="text-slate-400 font-normal">(Optional)</span></span>}
                placeholder="A brief summary of your professional background..."
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                maxLength={500}
                rows={3}
              />

              <div className="pt-6 flex items-center justify-between border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setStep(1)} disabled={loading}>
                  Back
                </Button>
                <Button type="submit" isLoading={loading} size="lg">
                  Save & Continue
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Completion */}
        {step === 3 && (
          <div className="text-center py-12 animate-in zoom-in-95 duration-500">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-8">
              <SafeIcon icon={FiIcons.FiCheck} className="text-4xl text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Your Meetoi profile is ready
            </h2>
            <p className="text-lg text-slate-600 max-w-sm mx-auto mb-10">
              You can now create your first digital networking card.
            </p>
            <Button size="lg" onClick={() => navigate('/app/dashboard')} className="w-full sm:w-auto px-12">
              Go to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};